import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function MiddlesbroughCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Middlesbrough Pothole Claims | 26% Estate Roads in RED | Fixtyer"
        description="Middlesbrough earns GREEN DfT scorecards for spend and best practice, yet 26% of its 547km unclassified road network is in RED condition. See what the council's own transparency report means for your pothole claim."
        canonical="/councils/middlesbrough"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Middlesbrough', url: '/councils/middlesbrough' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Middlesbrough has a GREEN overall DfT rating — can I still claim?',
            answer: 'Yes. The overall GREEN rating reflects strong spend and best-practice scorecards, but the Condition scorecard is AMBER — driven by unclassified roads where 26% are in RED condition and the council admits funding is insufficient to maintain the network. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on the headline scorecard colour.',
          },
          {
            question: 'What if my pothole was on a residential or estate road?',
            answer: 'U-roads make up 547km — roughly 85% of Middlesbrough\'s 647km adopted carriageway network. The council\'s own report shows 26% of unclassified roads in RED condition in 2024, up from 10% in 2015, with a spike to 35% in 2022. Unlike classified roads, the published data for U-roads only reports the RED percentage — not amber or green splits — but the trend the council documents is a steady decline.',
          },
          {
            question: 'Does the jump to 35% RED U-roads in 2022 weaken my claim?',
            answer: 'No — it strengthens the structural argument. Middlesbrough\'s CVI survey recorded 35% of unclassified roads in RED condition in 2022, more than double the 17% recorded in 2021. The figure fell to 26% in 2023 and 2024, but the council itself states the trend on unclassified roads is showing a steady decline and that available funding is insufficient to maintain the network in steady condition.',
          },
          {
            question: 'Middlesbrough spends nearly triple its DfT allocation — does that block my claim?',
            answer: 'Not automatically. The council projects £9.234m capital spend against a £3.339m DfT allocation in 2025/26, with 90% classed as preventative maintenance. But much of that investment targets classified main roads — where RED rates are under 1.2% — and bridges and structures (£4.332m alone). The AMBER condition rating and the council\'s admission that estate-road funding is insufficient tell a different story for residential streets.',
          },
          {
            question: 'Are A-roads and trunk routes still Middlesbrough Council?',
            answer: 'Not all of them. The A19 and A174 trunk roads through Middlesbrough are maintained by National Highways. The A66 dual carriageway — which serves Teesworks, the largest freeport in the country — is maintained by Middlesbrough Council. Check which authority maintains the road where your incident occurred before claiming.',
          },
          {
            question: 'Should I report the pothole to Middlesbrough before claiming?',
            answer: 'Yes. Reporting creates a dated council record — useful if the defect was reported before your incident, or if the council failed to repair it within a reasonable time. Use the council\'s online report service for road, pavement and cycleway problems, or call 01642 726001. Keep your reference number and any confirmation emails.',
          },
          {
            question: 'Does the 23,560 potholes filled in 2020/21 mean the roads were fixed?',
            answer: 'That year was an outlier. Middlesbrough undertook a full-network pothole survey and ward-by-ward patching programme funded by additional DfT/TVCA capital, employing extra agency staff — the council says this resulted in three times as many potholes being filled as an average year. Normal years since have ranged from 5,268 to 6,868 repairs. Reactive patching does not necessarily mean the underlying carriageway condition has improved.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Middlesbrough Council"
        councilSlug="middlesbrough"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/middlesbrough"
        headline="Middlesbrough Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Middlesbrough</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            One in four Middlesbrough estate roads needs resurfacing
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Middlesbrough earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards for spend
            and best practice, and its classified main roads are among the best in the country. Yet the overall
            condition rating is <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">26% of its 547km unclassified road network</strong> is in RED
            condition, and the council itself admits available funding is insufficient to stop the decline.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">26%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 10% in 2015 and 17% in 2021 — with a spike to 35% in 2022. Estate roads make up
              547km, roughly 85% of Middlesbrough&apos;s 647km carriageway network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Two networks, two stories
          </h2>
          <p className="text-surface-600 mb-8">
            Ten years of SCANNER and CVI survey data from Middlesbrough&apos;s own transparency report —
            main roads thriving, estate streets slipping
          </p>

          {/* Classified roads summary */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">
              Classified roads (100km — 15% of network): among the best in England
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center bg-white rounded-xl p-4 border border-emerald-200">
                <div className="text-3xl font-bold text-emerald-600">0.8%</div>
                <div className="text-sm text-emerald-800">A-roads RED (2024)</div>
                <div className="text-xs text-surface-500">34km network</div>
              </div>
              <div className="text-center bg-white rounded-xl p-4 border border-emerald-200">
                <div className="text-3xl font-bold text-emerald-600">0.9%</div>
                <div className="text-sm text-emerald-800">B-roads RED (2024)</div>
                <div className="text-xs text-surface-500">31km network</div>
              </div>
              <div className="text-center bg-white rounded-xl p-4 border border-emerald-200">
                <div className="text-3xl font-bold text-emerald-600">1.2%</div>
                <div className="text-sm text-emerald-800">C-roads RED (2024)</div>
                <div className="text-xs text-surface-500">35km network</div>
              </div>
            </div>
            <p className="text-emerald-800 text-sm">
              The council states its red-rated classified roads are &ldquo;one of the best in the country&rdquo;,
              with recent investment producing &ldquo;great benefits for the town.&rdquo; A-roads have held at
              0.8% RED since 2023, with 87.9% in green condition.
            </p>
          </div>

          {/* A-road trend table */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-road condition trend (34km)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2015</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">21.5%</td>
                    <td className="px-4 py-2 text-center">75.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">16.7%</td>
                    <td className="px-4 py-2 text-center">81.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.0%</td>
                    <td className="px-4 py-2 text-center">11.6%</td>
                    <td className="px-4 py-2 text-center">87.4%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">0.8%</td>
                    <td className="px-4 py-2 text-center font-bold">11.3%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">87.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: the A66 and other classified routes serving Teesworks and the town centre
              have genuinely improved. But classified roads are just 100km of a 647km network.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend, AMBER condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.3m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£9.2m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">90%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Middlesbrough invests nearly <span className="text-emerald-400">triple its DfT allocation</span>{' '}
              — yet the DfT still rates condition AMBER. The council directs much of that spend to classified
              roads and structures; estate roads, where the deterioration is documented, carry the weight of
              the AMBER rating.
            </p>
          </div>
        </div>
      </section>

      {/* The 547km estate road problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 547km estate road network
          </h2>
          <p className="text-surface-600 mb-8">
            85% of Middlesbrough&apos;s carriageways are unclassified — and the council publishes only RED
            condition data for them
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2015</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2018</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17.0%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26.0%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What 26% RED means in kilometres</h3>
              <p className="text-surface-600 text-sm mb-3">
                Middlesbrough maintains 547km of unclassified estate roads. At 26% RED condition, roughly{' '}
                <strong>142km of residential streets, cul-de-sacs and local routes</strong> require planned
                resurfacing maintenance under the council&apos;s own CVI survey classification.
              </p>
              <p className="text-surface-600 text-sm">
                The council surveys unclassified roads annually using a two-person CVI (Coarse Visual
                Inspection) team who record defects manually — the same red/amber/green system as SCANNER,
                but the published transparency report only discloses the RED percentage for U-roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2022 spike</h3>
              <p className="text-amber-800 text-sm mb-3">
                RED-condition U-roads jumped from 17% in 2021 to 35% in 2022 — more than doubling in a
                single survey year. The figure fell to 26% in 2023 and held there in 2024, but remains
                well above the 10% recorded in 2015.
              </p>
              <p className="text-amber-800 text-sm">
                The council attributes classified-road improvement to concentrating treatment on amber sections.
                No equivalent statement is made about estate-road recovery — only that the trend is a steady
                decline.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Middlesbrough&apos;s estate-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road one of the roughly 142km in RED condition at the last CVI survey?</li>
              <li>• If 26% of U-roads need planned resurfacing, what preventative work was scheduled for yours?</li>
              <li>• Does reactive pothole patching on a RED-rated carriageway satisfy the Section 41 duty?</li>
              <li>• Can the council point to amber or green U-road data when it only publishes the RED percentage?</li>
            </ul>
            <p className="text-white font-medium">
              A council that documents insufficient funding to maintain 85% of its network cannot easily
              argue it had reasonable knowledge of every defect on every estate street.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            47,154 pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Middlesbrough&apos;s own transparency report — including one
            exceptional year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Notes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23,560</td>
                  <td className="px-6 py-4 text-surface-600">Full-network survey; ward-by-ward patching programme</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,868</td>
                  <td className="px-6 py-4 text-surface-600">&nbsp;</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,303</td>
                  <td className="px-6 py-4 text-surface-600">&nbsp;</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">5,268</td>
                  <td className="px-6 py-4 text-surface-600">&nbsp;</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">6,155</td>
                  <td className="px-6 py-4 text-surface-600">&nbsp;</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">47,154</td>
                  <td className="px-6 py-4 text-surface-600">&nbsp;</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2020/21 outlier</h3>
              <p className="text-surface-600 text-sm">
                Additional DfT/TVCA pothole funding and an £800k allocation funded a full-network pothole
                survey and ward-by-ward patching programme. The council employed extra agency staff and
                says this resulted in <strong>three times as many potholes being filled</strong> as an average
                year. Normal years since have averaged roughly 5,900 repairs.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 repeat planned</h3>
              <p className="text-amber-800 text-sm">
                Middlesbrough plans to repeat the borough-wide pothole patching approach for 2025/26,
                building it into its maintenance regime going forward. The estimated number of potholes to
                be filled will be determined once a new pothole survey is completed — the figure is not
                yet published.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The council&apos;s own assessment
          </h2>
          <p className="text-surface-600 mb-8">
            Middlesbrough&apos;s transparency report — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The trend on our unclassified roads is showing a steady decline in condition. This means that the funding made available is insufficient to maintain the network in a steady condition or improve its condition."
              source="Middlesbrough Council Local Authority Highway Maintenance Transparency Report 2025-26 (June 2025)"
            />
            <QuoteCard
              quote="The surfacing condition of the road network across the town, like many other towns across the country, is aged and in much need of preventative maintenance."
              source="Middlesbrough Council Local Authority Highway Maintenance Transparency Report 2025-26 (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Middlesbrough formally acknowledges that estate-road funding is insufficient to halt
                deterioration — while simultaneously earning GREEN spend and best-practice scorecards
                for its overall programme. That tension is exactly why the DfT rates condition AMBER despite
                a GREEN overall rating.
              </p>
              <p className="text-surface-600 text-sm">
                Documented knowledge of network-wide decline raises questions about whether reactive pothole
                patching satisfies the duty of reasonable care on roads the council knows are deteriorating.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 investment plans</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• 19.2km (12 miles) of carriageway surfacing planned</li>
                <li>• Borough-wide pothole patching programme</li>
                <li>• Year 1 of a 4-year bridges and structures programme</li>
                <li>• 18 footway renewal and 16 verge repair schemes</li>
                <li>• 90% preventative / 10% reactive maintenance split</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Funding breakdown */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Where the money goes
          </h2>
          <p className="text-surface-600 mb-8">
            2025/26 funding split from Middlesbrough&apos;s transparency report — total projected spend £10.4m
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset type</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">DfT capital (£000s)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Total spend (£000s)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Carriageways (preventative &amp; reactive)</td>
                  <td className="px-6 py-4 text-center">£2,139</td>
                  <td className="px-6 py-4 text-center font-bold">£5,248</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Bridges &amp; structures</td>
                  <td className="px-6 py-4 text-center">£200</td>
                  <td className="px-6 py-4 text-center font-bold">£4,532</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Street lighting</td>
                  <td className="px-6 py-4 text-center">£200</td>
                  <td className="px-6 py-4 text-center">£1,163</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center">£500</td>
                  <td className="px-6 py-4 text-center">£1,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Other highway ancillaries (revenue)</td>
                  <td className="px-6 py-4 text-center">—</td>
                  <td className="px-6 py-4 text-center">£1,138</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total</td>
                  <td className="px-6 py-4 text-center font-bold">£3,339</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£10,372</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm">
            Bridges and structures receive £4.532m — nearly as much as all carriageway work combined.
            The council maintains 222 highway structures including 42 road bridges, and is undertaking
            works on Newport Bridge, the A174 Marton Interchange, and planning for the Tees Transporter
            Bridge and three A66 interchanges. That investment explains the GREEN spend scorecard — but
            much of it does not touch the estate roads where condition is AMBER.
          </p>
        </div>
      </section>

      {/* What GREEN + AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-rated, AMBER-conditioned council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Middlesbrough is well-run on paper — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — nearly triple DfT allocation projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — NHT benchmarking, 90% preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Classified roads among the best RED rates in England (under 1.2%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual CVI surveys on the full unclassified network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented streetworks permit scheme with penalty enforcement
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on classified roads and the A66. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 26% of 547km U-roads in RED, up from 10% in 2015
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits funding is insufficient to maintain estate roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 85% of the network is unclassified — where deterioration is documented
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 47,154 pothole repairs in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Much capital spend directed to bridges and classified roads, not estate streets
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards, your claim lives or dies on
              the <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the 26% RED rate and funding-insufficiency admission are your strongest arguments</li>
              <li>• Whether the road is council-maintained (A66) or National Highways (A19, A174)</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Middlesbrough&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Middlesbrough first
          </h2>
          <p className="text-surface-600 mb-6">
            Middlesbrough&apos;s transparency report covers reactive maintenance undertaken in line with
            Section 41 and Section 58 statutory undertakings. Reporting the defect through the council
            creates a dated record — useful evidence if the pothole was reported before your incident, or
            if the council failed to repair it within a reasonable time.
          </p>
          <a
            href="https://www.middlesbrough.gov.uk/report/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Middlesbrough Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Select &ldquo;Road, pavement, and cycleway problems&rdquo; on the council report page, or call
            01642 726001. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Middlesbrough?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road decline argument</li>
                <li>• No prior-report search</li>
                <li>• No funding-insufficiency citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 26% U-road RED condition documented</li>
                <li>✅ Council funding admission cited</li>
                <li>✅ 47,154 repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Middlesbrough</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=middlesbrough"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Middlesbrough claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Middlesbrough has a GREEN overall DfT rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The overall GREEN rating reflects strong spend and best-practice scorecards, but the
                Condition scorecard is AMBER — driven by unclassified roads where 26% are in RED condition
                and the council admits funding is insufficient to maintain the network. Section 58 turns on
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired,
                not on the headline scorecard colour.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or estate road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 547km — roughly 85% of Middlesbrough&apos;s 647km adopted carriageway network.
                The council&apos;s own report shows 26% of unclassified roads in RED condition in 2024, up from
                10% in 2015, with a spike to 35% in 2022. Unlike classified roads, the published data for
                U-roads only reports the RED percentage — not amber or green splits — but the trend the council
                documents is a steady decline.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the jump to 35% RED U-roads in 2022 weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it strengthens the structural argument. Middlesbrough&apos;s CVI survey recorded 35% of
                unclassified roads in RED condition in 2022, more than double the 17% recorded in 2021. The
                figure fell to 26% in 2023 and 2024, but the council itself states the trend on unclassified
                roads is showing a steady decline and that available funding is insufficient to maintain the
                network in steady condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Middlesbrough spends nearly triple its DfT allocation — does that block my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. The council projects £9.234m capital spend against a £3.339m DfT allocation
                in 2025/26, with 90% classed as preventative maintenance. But much of that investment targets
                classified main roads — where RED rates are under 1.2% — and bridges and structures (£4.332m
                alone). The AMBER condition rating and the council&apos;s admission that estate-road funding is
                insufficient tell a different story for residential streets.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Are A-roads and trunk routes still Middlesbrough Council?
              </h3>
              <p className="text-surface-600 text-sm">
                Not all of them. The A19 and A174 trunk roads through Middlesbrough are maintained by National
                Highways. The A66 dual carriageway — which serves Teesworks, the largest freeport in the country
                — is maintained by Middlesbrough Council. Check which authority maintains the road where your
                incident occurred before claiming.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Should I report the pothole to Middlesbrough before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Reporting creates a dated council record — useful if the defect was reported before your
                incident, or if the council failed to repair it within a reasonable time. Use the council&apos;s
                online report service for road, pavement and cycleway problems, or call 01642 726001. Keep your
                reference number and any confirmation emails.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 23,560 potholes filled in 2020/21 mean the roads were fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                That year was an outlier. Middlesbrough undertook a full-network pothole survey and ward-by-ward
                patching programme funded by additional DfT/TVCA capital, employing extra agency staff — the
                council says this resulted in three times as many potholes being filled as an average year.
                Normal years since have ranged from 5,268 to 6,868 repairs. Reactive patching does not
                necessarily mean the underlying carriageway condition has improved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.middlesbrough.gov.uk/parking-roads-and-transport/transport-strategies-and-standards/highway-asset-management-and-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Middlesbrough Council — Highway asset management and policy
            </a>{' '}
            |{' '}
            <a
              href="https://www.middlesbrough.gov.uk/media/vwfhsnhh/dft-highway-funding-transparency-report-2025-2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Local Authority Highway Maintenance Transparency Report 2025-26 (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
